/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {List, makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {forceAmericaPrimaryProperties, forceAmericaSecondaryProperties, GenericObject} from '@ion/core/src/types/entities';
import {timetoDate} from '@ion/core/src/utils/DateUtil';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import {setPriorityOfProperties} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {formatForceAmericaData} from './spreaderUtils';

export interface ForceAmericaCardListContentProps {
  measures: GenericObject
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  list : {
    paddingBottom : '0px'
  }
}));

export const ForceAmericaCardListContent = ({measures}: ForceAmericaCardListContentProps) => {
  const classes = useStyles();
  const noValue = translate('common:notAvailable');
  const {granularMaterialSetting, granularRateIndex} = forceAmericaPrimaryProperties;
  /**
   * getForceAmericaProperties is a function that compares the properties included
   * in the measures object with the properties in forceAmericaPrimaryProperties.
   * If some properties are not included in the measures object, we need to return
   * the difference so that all properties are displayed in the SpreaderMessageAccordion component.
   */
  const getForceAmericaProperties = useCallback((knownProperties) => {
    const allProperties       = Object.keys(forceAmericaPrimaryProperties);
    const secondaryProperties = Object.keys(forceAmericaSecondaryProperties);
    const actualProperties    = Object.keys(knownProperties)
      ?.filter(property => !secondaryProperties.includes(property));
    const remainingProperties = allProperties
      ?.filter(property => !actualProperties.includes(property))
      ?.map(property => property);
    const properties = [...actualProperties, ...remainingProperties].sort().reverse();
    return setPriorityOfProperties(
      properties,
      granularMaterialSetting,
      granularRateIndex
    );
  }, [measures, granularMaterialSetting, granularRateIndex]);

  const forceAmericaProperties = useMemo(() => {
    let secondaryProperties = Object.keys(forceAmericaSecondaryProperties)
      ?.filter(property => !isNil(measures?.[property]));
    const {date, time} = forceAmericaSecondaryProperties;
    if (measures && secondaryProperties.includes(time) &&
      secondaryProperties.includes(date)) {
      const dateTimeValue = timetoDate(
        measures[time],
        measures[date],
        'MM/DD/YY'
      );
      measures[date] = getFormattedDate(dateTimeValue);
      secondaryProperties = secondaryProperties.filter(property => property !== time);
    }
    return [...getForceAmericaProperties(measures), ...secondaryProperties];
  }, [measures]);

  return (
    <List className={classes.list}>
      {forceAmericaProperties.map((key: string) => {
        const value = measures[key];
        return (
          <CardItemRow key={key}
            label={translate(`tracking:spreader.FORCE_AMERICA.${key}`)}
            value={!isNil(value) ? formatForceAmericaData(key, value) : noValue}
            isCenterAligned={false}
            isBottomMargin={false}
            isTextRightAligned
            isCondensedVariant />
        );
      }
      )}
    </List>
  );
};

export default ForceAmericaCardListContent;
